import React, { useState, useEffect, useMemo } from "react";
import { useSupabase } from "../SupabaseContext";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "./ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Loader2,
  Search,
  UserCheck,
  MessageSquare,
  CheckCircle,
  ArrowDown,
  ArrowRightToLine,
  XCircle,
} from "lucide-react";
import { Alert, AlertDescription } from "./ui/alert";
import { Badge } from "./ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import EmptyStateMessage from "./EmptyStateMessage";
import AcceptanceDistribution from "./visualizations/AcceptanceDistribution";

import SelfAssessmentChange from "./visualizations/SelfAssessmentChange";
import SelfSelectionDistribution from "./visualizations/SelfSelectionDistribution";
import GroupComparisonChart from "./GroupComparisonChart";
import RankingChart from "./visualizations/RankingChart";
import EvaluationRelationships from "./visualizations/EvaluationRelationships";
import AISummaryCard from "./AISummaryCard";
import { Switch } from "./ui/switch";
import { Label as UILabel } from "./ui/label";

const EvaluationMatrix = () => {
  const [user] = useState(JSON.parse(localStorage.getItem("user")));

  const supabase = useSupabase();
  const [situations, setSituations] = useState([]);
  const [selectedSituation, setSelectedSituation] = useState("");
  const [facilities, setFacilities] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState("");
  const [matrixData, setMatrixData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [acceptanceData, setAcceptanceData] = useState([]);
  const [selfSelectionData, setSelfSelectionData] = useState([]);
  const [rankingData, setRankingData] = useState([]);
  const [selfAssessmentChangeData, setSelfAssessmentChangeData] = useState([]);
  const [selfSelectionDetailData, setSelfSelectionDetailData] = useState([]);
  const [userResponses, setUserResponses] = useState([]);
  const [excludeSelfAssessmentMatrix, setExcludeSelfAssessmentMatrix] =
    useState(false);

  const [selectedSituationDetails, setSelectedSituationDetails] =
    useState(null);

  const [evaluationData, setEvaluationData] = useState({
    solutions: [],
    aiSolution: null,
    isLoading: false,
    error: null,
  });

  // Add our hasData check
  // Replace the existing hasData check in EvaluationMatrix.js with this improved version:

  const hasData = useMemo(() => {
    // Check if there are solutions
    const hasSolutions = evaluationData.solutions?.length > 0;

    // Check if there are actual evaluations (scores) in the matrix
    const hasEvaluations = matrixData.some((row) => {
      return Object.entries(row).some(
        ([key, value]) =>
          // Only count numeric values (actual scores)
          typeof value === "number" &&
          // Skip id, name, hasAnswered, isAI, isAverage, average fields
          ![
            "id",
            "name",
            "hasAnswered",
            "isAI",
            "isAverage",
            "average",
          ].includes(key)
      );
    });

    return hasSolutions && matrixData.length > 0 && hasEvaluations;
  }, [evaluationData.solutions, matrixData]);

  useEffect(() => {
    fetchSituations();
    fetchFacilities();
  }, []);

  useEffect(() => {
    if (selectedSituation && selectedFacility) {
      fetchEvaluations();
      fetchUserResponses();
      fetchSituationDetails();
    }
  }, [selectedSituation, selectedFacility]);

  // Add this effect to update matrixData when toggle changes
  useEffect(() => {
    if (evaluationData && evaluationData.solutions && evaluationData.users) {
      const matrix = processMatrixDataForTable(
        evaluationData.solutions,
        evaluationData.users,
        excludeSelfAssessmentMatrix
      );
      setMatrixData(matrix);
    }
  }, [evaluationData, excludeSelfAssessmentMatrix]);

  const fetchSituationDetails = async () => {
    if (!selectedSituation) return;

    try {
      const { data, error } = await supabase
        .from("situations")
        .select("title, description")
        .eq("id", selectedSituation)
        .single();

      if (error) throw error;
      setSelectedSituationDetails(data);
    } catch (error) {
      console.error("Error fetching situation details:", error);
      setError("Failed to load situation details. Please try again.");
    }
  };

  const fetchSituations = async () => {
    const { data, error } = await supabase
      .from("situations")
      .select("id, title")
      .eq("creator_id", user.id); // Only get situations created by current user

    if (error) console.error("Chyba pri načítaní situácií:", error);
    else setSituations(data);
  };

  const fetchFacilities = async () => {
    try {
      let query = supabase
        .from("facilities")
        .select("id, name")
        .eq("is_active", true);

      if (user.is_manager) {
        // If user is a manager, get their managed facilities
        const { data: managedFacilities } = await supabase
          .from("facility_managers")
          .select("facility_id")
          .eq("user_id", user.id)
          .eq("is_active", true);

        query = query.in(
          "id",
          managedFacilities.map((f) => f.facility_id)
        );
      }

      const { data, error } = await query;
      if (error) throw error;
      setFacilities(data);
    } catch (error) {
      console.error("Chyba pri načítaní skupín:", error);
      setError("Nepodarilo sa načítať skupiny.");
    }
  };

  // Inside EvaluationMatrix.js

  // Add a new state for the unified evaluation data

  // Update the fetchEvaluations function to get all data at once
  const fetchEvaluations = async () => {
    setEvaluationData((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      // First fetch all users who have access to this situation
      const { data: users, error: usersError } = await supabase
        .from("access_control")
        .select(
          `
        user_id,
        users!inner (
          id,
          first_name,
          last_name,
          solutions (
            id,
            situation_id
          )
        )
      `
        )
        .eq("situation_id", selectedSituation)
        .eq("facility_id", selectedFacility);

      if (usersError) throw usersError;

      // Fetch solutions with their evaluations in a single query
      const { data: solutions, error: solutionsError } = await supabase
        .from("solutions")
        .select(
          `
        id,
        user_id,
        content,
        users (
          id,
          first_name,
          last_name
        ),
        evaluations (
          id, 
          evaluator_id, 
          score,
          users (
            id,
            first_name,
            last_name
          )
        )
      `
        )
        .eq("situation_id", selectedSituation);

      if (solutionsError) throw solutionsError;

      // Process solutions to separate AI solution
      let aiSolution = solutions.find((s) => s.user_id === null);
      let userSolutions = solutions.filter((s) => s.user_id !== null);

      // Ensure AI solution has a self-evaluation with score 5
      if (aiSolution) {
        const hasSelfEval = aiSolution.evaluations.some(
          (e) => e.evaluator_id === null
        );
        if (!hasSelfEval) {
          aiSolution = {
            ...aiSolution,
            evaluations: [
              ...aiSolution.evaluations,
              {
                id: "ai-self-eval",
                evaluator_id: null,
                score: 5,
                users: null,
              },
            ],
          };
        }
      }

      // Add AI solution to the list for ranking and charts
      const allSolutions = aiSolution
        ? [...userSolutions, aiSolution]
        : userSolutions;

      // Extract self-evaluation details - ADD THIS ENTIRE BLOCK
      const selfEvaluationDetails = [];
      allSolutions.forEach((solution) => {
        // For AI, treat evaluator_id === null as self-evaluation
        const selfEvaluation = solution.evaluations.find(
          (e) =>
            e.evaluator_id === solution.user_id ||
            (solution.user_id === null && e.evaluator_id === null)
        );
        if (!selfEvaluation) return;
        const userName =
          solution.user_id === null
            ? "AI Riešenie"
            : solution.users
            ? `${solution.users.first_name} ${solution.users.last_name}`
            : "Unknown";
        selfEvaluationDetails.push({
          name: userName,
          selfScore: selfEvaluation.score,
        });
      });

      // Set the self-evaluation details - ADD THIS LINE
      setSelfSelectionDetailData(selfEvaluationDetails);

      // Save all data in a single state
      setEvaluationData({
        solutions: allSolutions,
        aiSolution: aiSolution,
        users,
        isLoading: false,
        error: null,
      });

      // Process the matrix data as you were doing before
      // We keep this separate since the matrix visualization is different
      const matrix = processMatrixDataForTable(
        solutions,
        users,
        excludeSelfAssessmentMatrix
      );
      setMatrixData(matrix);
    } catch (error) {
      console.error("Error fetching evaluations:", error);
      setEvaluationData((prev) => ({
        ...prev,
        isLoading: false,
        error: "Nepodarilo sa načítať hodnotenia. Skúste to prosím znova.",
      }));
    }
  };

  // Define a function to process the matrix data specifically for the table view
  const processMatrixDataForTable = (solutions, users, excludeSelf) => {
    // Create matrix with all users who have access
    const matrix = users.map((accessRecord) => {
      const user = accessRecord.users;
      const row = {
        id: user.id,
        name: `${user.first_name} ${user.last_name}`,
        hasAnswered: user.solutions.some(
          (s) => s.situation_id === selectedSituation
        ),
        isAI: false,
      };

      // Add evaluations for each user
      users.forEach((otherAccess) => {
        const otherUser = otherAccess.users;
        const solution = solutions.find((s) => s.user_id === user.id);
        if (solution) {
          let evaluation = solution.evaluations.find(
            (e) => e.evaluator_id === otherUser.id
          );
          // Exclude self-assessment if needed
          if (excludeSelf && user.id === otherUser.id) {
            evaluation = null;
          }
          row[otherUser.id] = evaluation ? evaluation.score : null;
        } else {
          row[otherUser.id] = null;
        }
      });

      // Calculate row average (Priemer)
      const scores = Object.entries(row)
        .filter(
          ([key, value]) =>
            // Only include numeric scores and exclude id, name, hasAnswered, isAI fields
            typeof value === "number" &&
            !["id", "name", "hasAnswered", "isAI"].includes(key)
        )
        .map(([, score]) => score);

      row.average =
        scores.length > 0
          ? (
              scores.reduce((sum, score) => sum + score, 0) / scores.length
            ).toFixed(2)
          : null;

      return row;
    });

    // Add AI solution if it exists
    const aiSolution = solutions.find((s) => s.user_id === null);
    if (aiSolution) {
      const aiRow = {
        id: "ai",
        name: "AI Riešenie",
        isAI: true,
      };
      users.forEach((accessRecord) => {
        const user = accessRecord.users;
        let evaluation = aiSolution.evaluations.find(
          (e) => e.evaluator_id === user.id
        );
        // Exclude self-assessment for AI if needed (AI self-eval is evaluator_id === null)
        if (excludeSelf && user.id === null) {
          evaluation = null;
        }
        aiRow[user.id] = evaluation ? evaluation.score : null;
      });

      // Calculate AI row average
      const aiScores = Object.entries(aiRow)
        .filter(
          ([key, value]) =>
            typeof value === "number" && !["id", "name", "isAI"].includes(key)
        )
        .map(([, score]) => score);

      aiRow.average =
        aiScores.length > 0
          ? (
              aiScores.reduce((sum, score) => sum + score, 0) / aiScores.length
            ).toFixed(2)
          : null;

      matrix.push(aiRow);
    }

    // Add average row calculation
    const averageRow = {
      id: "average",
      name: "Priemer",
      isAverage: true,
    };

    // Calculate column averages
    users.forEach((accessRecord) => {
      const user = accessRecord.users;
      const scores = matrix
        .filter((row) => !row.isAverage)
        .map((row) => row[user.id])
        .filter((score) => score !== null);

      averageRow[user.id] =
        scores.length > 0
          ? (
              scores.reduce((sum, score) => sum + score, 0) / scores.length
            ).toFixed(2)
          : null;
    });

    // Calculate overall average for the average row
    const averageScores = Object.entries(averageRow)
      .filter(
        ([key, value]) =>
          typeof value === "number" &&
          !["id", "name", "isAverage"].includes(key)
      )
      .map(([, score]) => parseFloat(score));

    averageRow.average =
      averageScores.length > 0
        ? (
            averageScores.reduce((sum, score) => sum + score, 0) /
            averageScores.length
          ).toFixed(2)
        : null;

    matrix.push(averageRow);

    return matrix;
  };

  const fetchUserResponses = async () => {
    if (!selectedSituation || !selectedFacility) return;

    try {
      // Get all solutions for this situation
      const { data: solutionsData, error: solutionsError } = await supabase
        .from("solutions")
        .select(
          `
        id,
        content,
        user_id,
        users (
          first_name,
          last_name
        )
      `
        )
        .eq("situation_id", selectedSituation);

      if (solutionsError) throw solutionsError;

      // Get users who have access to this situation in the selected facility
      // Use the same approach as fetchEvaluations
      const { data: accessData, error: accessError } = await supabase
        .from("access_control")
        .select("user_id")
        .eq("situation_id", selectedSituation)
        .eq("facility_id", selectedFacility);

      if (accessError) throw accessError;

      if (!accessData || accessData.length === 0) {
        console.log(
          "No users found with access to this situation/facility combination"
        );
        setUserResponses([]);
        return;
      }

      // Extract the user IDs from access control
      const accessUserIds = accessData.map((access) => access.user_id);

      // Filter solutions to show those from users who have access to this situation/facility
      // Include AI solution (where user_id is null) and solutions from users with access
      const filteredSolutions = solutionsData.filter(
        (solution) =>
          solution.user_id === null || // Include AI solutions
          accessUserIds.includes(solution.user_id) // Include solutions from users with access
      );

      setUserResponses(filteredSolutions);
    } catch (error) {
      console.error("Error fetching user responses:", error);
      setError("Nepodarilo sa načítať odpovede používateľov");
    }
  };

  const getSelectedSituationTitle = () => {
    return situations.find((s) => s.id === selectedSituation)?.title || "";
  };

  const getSelectedCompanyName = () => {
    return facilities.find((c) => c.id === selectedFacility)?.name || "";
  };

  const getCellColor = (value) => {
    if (value === null) return "bg-gray-100";
    const score = parseFloat(value);
    if (score === 0) return "bg-gray-700 text-white";
    if (score < 2) return "bg-red-100 text-red-800";
    if (score < 3) return "bg-orange-100 text-orange-800";
    if (score < 4) return "bg-yellow-100 text-yellow-800";
    if (score < 5) return "bg-amber-200 text-amber-800"; // Strong yellow (amber)
    return "bg-green-100 text-green-800";
  };

  const filteredMatrixData = useMemo(
    () =>
      matrixData.filter((row) =>
        row.name.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [matrixData, searchTerm]
  );

  const UserResponsesTable = () => {
    const truncateText = (text, maxLength) => {
      if (text.length <= maxLength) return text;
      return text.substr(0, maxLength) + "...";
    };

    return (
      <Card className="mt-8">
        <CardHeader>
          <CardTitle className="flex items-center">
            <MessageSquare className="mr-2 w-5 h-5" />
            Odpovede používateľov
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Meno</TableHead>
                <TableHead>Odpoveď</TableHead>
                <TableHead>Akcie</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {userResponses.map((response) => (
                <TableRow key={response.id}>
                  <TableCell>
                    {response.users?.first_name && response.users?.last_name
                      ? `${response.users.first_name} ${response.users.last_name}`
                      : "AI odpoveď"}
                  </TableCell>
                  <TableCell className="max-w-md">
                    <p className="text-sm text-gray-600">
                      {truncateText(response.content, 100)}
                    </p>
                  </TableCell>
                  <TableCell>
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="outline" size="sm">
                          Zobraziť celú odpoveď
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-3xl">
                        <DialogHeader>
                          <DialogTitle>
                            {response.users?.first_name &&
                            response.users?.last_name
                              ? `Odpoveď používateľa ${response.users.first_name} ${response.users.last_name}`
                              : "AI odpoveď"}
                          </DialogTitle>
                        </DialogHeader>
                        <div className="mt-4 max-h-[60vh] overflow-y-auto">
                          <ReactQuill
                            value={response.content}
                            readOnly={true}
                            theme="bubble"
                            modules={{ toolbar: false }}
                            className="[&_.ql-editor]:p-0 [&_.ql-editor]:text-sm"
                          />
                        </div>
                      </DialogContent>
                    </Dialog>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="w-full">
      <div className="flex flex-col mb-6 space-y-4 sm:flex-row sm:space-y-0 sm:space-x-4">
        <Select onValueChange={setSelectedSituation} value={selectedSituation}>
          <SelectTrigger className="w-full sm:w-[200px]">
            <SelectValue placeholder="Vyberte situáciu" />
          </SelectTrigger>
          <SelectContent>
            {situations.map((situation) => (
              <SelectItem key={situation.id} value={situation.id}>
                {situation.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select onValueChange={setSelectedFacility} value={selectedFacility}>
          <SelectTrigger className="w-full sm:w-[200px]">
            <SelectValue placeholder="Vyberte skupinu" />
          </SelectTrigger>
          <SelectContent>
            {facilities.map((facility) => (
              <SelectItem key={facility.id} value={facility.id}>
                {facility.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          onClick={fetchEvaluations}
          disabled={!selectedSituation || !selectedFacility}
          className="w-full sm:w-auto"
        >
          Zobraziť hodnotenia
        </Button>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {!selectedSituation || !selectedFacility ? (
        <div className="flex justify-center items-center h-64">
          <EmptyStateMessage></EmptyStateMessage>
        </div>
      ) : (
        <>
          {selectedSituationDetails && (
            <Card className="mb-6">
              <CardHeader className="pb-3 mb-3">
                <CardTitle className="text-xl font-semibold">
                  {selectedSituationDetails.title}
                </CardTitle>
                <CardDescription>Popis situácie</CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="prose max-w-none [&_.ql-editor]:px-0 [&_.ql-editor]:py-0">
                  <ReactQuill
                    value={selectedSituationDetails.description}
                    readOnly={true}
                    theme="bubble"
                    modules={{ toolbar: false }}
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Add toggle above the matrix table */}
          <div className="flex gap-2 items-center mb-2">
            <Switch
              id="exclude-self-assessment-matrix"
              checked={excludeSelfAssessmentMatrix}
              onCheckedChange={setExcludeSelfAssessmentMatrix}
              className="data-[state=checked]:bg-blue-600"
            />
            <UILabel
              htmlFor="exclude-self-assessment-matrix"
              className="flex items-center text-sm font-medium cursor-pointer"
            >
              Bez sebahodnotenia
            </UILabel>
          </div>

          <div className="overflow-x-auto mb-8 rounded-lg border shadow">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-100">
                  <TableHead className="font-bold text-gray-800 sticky left-0 bg-gray-100 z-20 min-w-[200px]">
                    <div className="flex flex-col items-center text-center">
                      <span className="text-gray-900">Zamestnanec</span>
                      <div className="flex gap-1 items-center mt-1 text-xs font-normal text-gray-500">
                        <span>Hodnotený</span>
                        <ArrowDown className="w-3 h-3" />
                      </div>
                    </div>
                  </TableHead>
                  <TableHead
                    className="font-bold text-center text-gray-800 border-b-2 border-gray-300 bg-blue-50/50"
                    colSpan={
                      matrixData.filter((e) => !e.isAI && !e.isAverage).length +
                      1
                    }
                  >
                    <div className="flex gap-2 justify-center items-center py-2">
                      <span className="text-gray-700">Hodnotitelia</span>
                      <ArrowRightToLine className="w-4 h-4 text-gray-700" />
                    </div>
                  </TableHead>
                </TableRow>
                <TableRow className="bg-gray-50">
                  <TableHead className="sticky left-0 z-20 bg-gray-50" />
                  {matrixData
                    .filter((employee) => !employee.isAI && !employee.isAverage)
                    .map((employee) => (
                      <TableHead
                        key={employee.id}
                        className="font-medium text-gray-700 min-w-[120px] px-2 bg-blue-50/50"
                      >
                        <div className="py-2 text-center line-clamp-2">
                          {employee.name}
                        </div>
                      </TableHead>
                    ))}
                  <TableHead className="font-medium text-gray-800 bg-gray-100">
                    <div className="flex flex-col gap-1 items-center py-2">
                      <span>Priemer</span>
                      <span className="text-xs text-gray-500">
                        všetkých hodnotení
                      </span>
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>

              <TableBody>
                {matrixData.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="sticky left-0 z-10 font-medium text-gray-800 bg-gray-50/80">
                      <div className="flex gap-2 items-center">
                        {row.isAverage ? (
                          <span className="font-bold text-gray-700">
                            Priemer hodnotení
                          </span>
                        ) : (
                          <>
                            <span>{row.name}</span>
                            {!row.isAI && !row.isAverage && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Badge
                                      variant={
                                        row.hasAnswered
                                          ? "success"
                                          : "destructive"
                                      }
                                      className={`px-2 py-0.5 text-xs ${
                                        row.hasAnswered
                                          ? "bg-green-50 text-green-700 border-green-200"
                                          : "bg-red-50 text-red-700 border-red-200"
                                      }`}
                                    >
                                      {row.hasAnswered ? (
                                        <div className="flex gap-1 items-center">
                                          <CheckCircle className="w-3 h-3" />
                                          <span>Odpovedal</span>
                                        </div>
                                      ) : (
                                        <div className="flex gap-1 items-center">
                                          <XCircle className="w-3 h-3" />
                                          <span>Neodpovedal</span>
                                        </div>
                                      )}
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent side="right">
                                    {row.hasAnswered
                                      ? "Používateľ odpovedal na túto situáciu"
                                      : "Používateľ zatiaľ neodpovedal na túto situáciu"}
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </>
                        )}
                      </div>
                    </TableCell>
                    {matrixData
                      .filter((col) => !col.isAI && !col.isAverage)
                      .map((col) => (
                        <TableCell
                          key={col.id}
                          className={`text-center font-medium relative ${getCellColor(
                            row[col.id]
                          )}`}
                        >
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="cursor-help">
                                  {row[col.id] !== null ? row[col.id] : "-"}
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="text-sm">
                                  <span className="font-medium">
                                    {row.name}
                                  </span>{" "}
                                  bol ohodnotený používateľom{" "}
                                  <span className="font-medium">
                                    {col.name}
                                  </span>
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </TableCell>
                      ))}
                    <TableCell
                      className={`text-center font-medium ${getCellColor(
                        row.average
                      )}`}
                    >
                      {row.average !== null ? row.average : "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {hasData ? (
            <>
              <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                <AcceptanceDistribution
                  evaluationData={evaluationData}
                  loading={evaluationData.isLoading}
                  error={evaluationData.error}
                />
                <SelfSelectionDistribution
                  evaluationData={evaluationData}
                  loading={evaluationData.isLoading}
                  error={evaluationData.error}
                />
                <SelfAssessmentChange
                  evaluationData={evaluationData}
                  loading={evaluationData.isLoading}
                  error={evaluationData.error}
                />
                <RankingChart
                  evaluationData={evaluationData}
                  loading={evaluationData.isLoading}
                  error={evaluationData.error}
                />

                <div className="md:col-span-2">
                  <EvaluationRelationships matrixData={matrixData} />
                </div>

                <div className="md:col-span-2">
                  <GroupComparisonChart
                    situationId={selectedSituation}
                    supabase={supabase}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-8 mt-8 md:grid-cols-2"></div>

              <Card className="mt-8">
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <UserCheck className="mr-2 w-5 h-5" />
                    Hodnotenia vlastného riešenia
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Meno</TableHead>
                        <TableHead>Sebahodnotenie</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selfSelectionDetailData.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>{item.name}</TableCell>
                          <TableCell>{item.selfScore}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              <UserResponsesTable />
              <AISummaryCard
                userResponses={userResponses}
                situationTitle={selectedSituationDetails?.title || ""}
                situationDescription={
                  selectedSituationDetails?.description || ""
                }
                situationId={selectedSituation}
                groupId={selectedFacility}
              />
            </>
          ) : (
            <div className="py-8">
              <Card>
                <CardContent className="p-8">
                  <div className="flex flex-col justify-center items-center space-y-3 text-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-10 h-10 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M20 12H4"
                      />
                    </svg>
                    <h3 className="text-lg font-medium text-gray-900">
                      Žiadne hodnotenia
                    </h3>
                    <p className="max-w-md text-gray-500">
                      Pre túto kombináciu situácie a skupiny nie sú dostupné
                      žiadne hodnotenia. Používatelia buď ešte neodpovedali,
                      alebo nie sú pridelené žiadne hodnotenia.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EvaluationMatrix;
